// src/app/events/session/stream/StreamSessionMedia.ts
import {
  AttachVideoRequest,
  ConnectionStatus,
  DetachVideoRequest,
  MediaDevice,
  UserIdType
} from '../model/SessionState';
import {SessionCommands} from '../model/SessionCommands';
import {SessionStore} from '../model/SessionStore';
import {Call, CallingState, StreamVideoParticipant} from '@stream-io/video-client';
import {StreamSessionParticipant} from './StreamSessionParticipant';
import {throttle} from 'rxjs/operators';
import {interval, Subscription} from 'rxjs';
import {AsyncSessionResult, SessionResult} from '../model/AsyncSessionResult';
import {logUtishaDebug, logUtishaError, logUtishaInfo, logUtishaTrace, logUtishaWarn} from 'src/app/util/log';
import {StreamMediaPublisher} from './StreamMediaPublisher';
import {MediaWatchdog, PublishRepairReason} from './MediaWatchdog';
import {SessionLifecycleGuardian} from './SessionLifecycleGuardian';
import {UtishaGeneralStore} from '../../../services/storage/general-store';
// import {NoiseCancellation} from '@stream-io/audio-filters-web';
import {NgZone} from '@angular/core';
import {TrackType} from '@stream-io/video-client/dist/src/gen/video/sfu/models/models';
import {SessionParticipant} from '../model/SessionParticipant';
import {SessionOperationQueue} from '../model/SessionOperationQueue';
import {SessionStateMachine} from '../model/SessionStateMachine';
import { PerformanceMonitor, CpuPressureLevel } from '../model/PerformanceMonitor';
import {SessionBreadcrumb} from './session-breadcrumbs';

type VoidFunction = () => void;

export class StreamSessionMedia {
  private activeSpeakerUpdateInterval = 1000;
  private videoBindingsCache = new Map<string, VoidFunction | undefined>();
  private videoTrackingCache = new Map<string, VoidFunction | undefined>();
  private audioBindingsCache = new Map<string, VoidFunction | undefined>();
  private subscriptions: Subscription[] = [];
  private callEventUnsubscribers: VoidFunction[] = [];
  private streamMediaPublisher: StreamMediaPublisher;
  private noiseCancellation: any;
  private containerElement: HTMLElement = document.body;
  // Shared AudioContext for all participant volume monitoring — browsers limit
  // AudioContext instances to ~6-8, so creating one per participant will crash.
  private sharedAudioContext: AudioContext | null = null;
  private audioMonitorSources = new Map<string, { source: MediaStreamAudioSourceNode; analyser: AnalyserNode }>();
  private operationQueue?: SessionOperationQueue;
  private deviceChangeHandler?: () => void;
  private trackEndedCleanups: VoidFunction[] = [];
  private wasReconnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private transientErrorCount = 0;
  private videoFailureCount = 0;
  private poorQualityCount = 0;
  private qualityReducedMode = false;
  private joinedAt = 0;
  private performanceMonitor = new PerformanceMonitor();
  private audioMonitorThrottleMs = 50;
  private _audioMonitorDestroyed = false;
  private selfViewElement: HTMLVideoElement | null = null;
  private audioFilterAppliedAt = 0;
  // Track-ended republish bookkeeping. Bounded retries so a genuinely dead
  // device doesn't loop, but transient OS preemptions (incoming call, USB
  // unplug/replug, permission toggle) silently recover.
  private cameraRepublishAttempts = 0;
  private micRepublishAttempts = 0;
  private cameraRepublishWindowStart = 0;
  private micRepublishWindowStart = 0;
  private cameraRepublishTimer: ReturnType<typeof setTimeout> | null = null;
  private micRepublishTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly TRACK_REPUBLISH_WINDOW_MS = 30_000;
  private readonly TRACK_REPUBLISH_MAX_PER_WINDOW = 3;
  private readonly TRACK_REPUBLISH_COOLDOWN_MS = 1_500;
  private readonly TRACK_REPUBLISH_MAX_BACKOFF_MS = 10_000;
  private mediaWatchdog?: MediaWatchdog;
  private lifecycleGuardian?: SessionLifecycleGuardian;

  constructor(
    private commands: SessionCommands,
    private sessionStore: SessionStore,
    public call: Call,
    private store: UtishaGeneralStore,
    private ngZone: NgZone,
    private sessionStateMachine?: SessionStateMachine
  ) {
  }

  async initialize(): Promise<void> {
    logUtishaDebug('stream: initialize devices');
    await this.initializeDevices();
    if (this.sessionStateMachine) {
      this.operationQueue = new SessionOperationQueue(this.sessionStateMachine);
    }
    logUtishaDebug('stream: register request handlers');
    this.registerRequestHandlers();
    // Initialize audio publisher
    this.streamMediaPublisher = new StreamMediaPublisher(this.call, this.commands, this.sessionStore);
    this.performanceMonitor.setCpuScoreProvider(() => this.streamMediaPublisher.getCpuUsageScore());
    this.streamMediaPublisher.onLocalStreamChanged = () => this.rebindSelfView();
    this.performanceMonitor.start();
    logUtishaDebug('stream: setup event handlers');
    this.setupEventListeners();
    logUtishaDebug('stream: setup device change listener');
    this.setupDeviceChangeListener();
    logUtishaDebug('stream: enable noise cancellation');
    this.enableNoiseCancellation();
    logUtishaDebug('stream: start media watchdog');
    this.startMediaWatchdog();
    logUtishaDebug('stream: start lifecycle guardian');
    this.startLifecycleGuardian();
    logUtishaDebug('stream: initialized media playback');
  }

  private startLifecycleGuardian(): void {
    this.lifecycleGuardian?.stop();
    this.lifecycleGuardian = new SessionLifecycleGuardian({
      forceHealthCheck: () => this.checkMediaHealthNow(),
      onPermissionChanged: (kind, state) => {
        if (state === 'denied') {
          // Don't loop republish attempts against a user who said no.
          if (kind === 'camera') {
            this.sessionStore.update({ localUser: { isVideoOn: false } });
            this.sessionStore.update({ session: { error: 'Camera permission was revoked' } });
          } else {
            this.sessionStore.update({ localUser: { isAudioOn: false, isMuted: true } });
            this.sessionStore.update({ session: { error: 'Microphone permission was revoked' } });
          }
        }
      },
      onCurrentDeviceVanished: (kind) => {
        // The current device is gone — let the republish scheduler find a
        // working one via the fallback ladder.
        if (kind === 'camera') {
          this.scheduleCameraRepublish('device-vanished');
        } else {
          this.scheduleMicRepublish('device-vanished');
        }
      },
      getCurrentCameraId: () => this.sessionStore.snapshot.localUser.camera?.deviceId,
      getCurrentMicrophoneId: () => this.sessionStore.snapshot.localUser.microphone?.deviceId,
    });
    this.lifecycleGuardian.start().catch(e =>
      logUtishaWarn('stream: lifecycle guardian start failed: ' + (e?.message || e))
    );
  }

  private startMediaWatchdog(): void {
    this.mediaWatchdog?.stop();
    this.mediaWatchdog = new MediaWatchdog({
      isVideoIntended: () => this.sessionStore.snapshot.localUser.isVideoOn === true,
      isAudioIntended: () => {
        const u = this.sessionStore.snapshot.localUser;
        return u.isAudioOn === true && u.isMuted !== true;
      },
      msSinceLastVideoFrame: () => this.streamMediaPublisher?.msSinceLastVideoFrame() ?? Infinity,
      isAudioAlive: () => {
        // Consider audio alive if the SDK thinks the mic is enabled AND a
        // live track exists on the published mediaStream. A track with
        // readyState 'ended' means the underlying source is gone even
        // though the SDK may still think it's enabled.
        if (this.call.microphone?.state?.status !== 'enabled') {
          // Not enabled → not our problem, isAudioIntended will be false too.
          return true;
        }
        const track = this.call.microphone.state.mediaStream?.getAudioTracks()?.[0];
        if (!track) return false;
        return track.readyState === 'live' && !track.muted;
      },
      isVideoAlive: () => {
        // Probe the raw camera source, not the canvas output — the canvas
        // will happily redraw the last frame even when the source is dead.
        const track = this.streamMediaPublisher?.sourceCameraVideoTrack ?? null;
        if (!track) return true; // Nothing to judge; stall check handles "pipeline not running".
        return track.readyState === 'live' && !track.muted;
      },
      requestCameraRepublish: (reason) => this.scheduleCameraRepublish(reason),
      requestMicRepublish: (reason) => this.scheduleMicRepublish(reason),
    });
    this.mediaWatchdog.start();
  }

  /** Exposed so the upcoming lifecycle guardian can force a check on wake. */
  checkMediaHealthNow(): void {
    this.mediaWatchdog?.checkNow();
  }

  private handleAttachVideo(payload: AttachVideoRequest) {
    logUtishaDebug('stream: handleAttachVideo');

    const {userId, element, elementType, quality} = payload;
    if (elementType !== 'video') {
      throw new Error('Invalid element type: ' + elementType);
    }
    const participant = this.sessionStore.snapshot.participants.find((p) => p.userId === userId) as StreamSessionParticipant;
    const videoElement = element as HTMLVideoElement;
    // For the local participant, bind directly to the publisher's video stream
    // instead of using call.bindVideoElement(), because video is published via
    // call.publish() (custom canvas stream) rather than call.camera.enable().
    if (participant?.isCurrentUser) {
      // Always store the video element reference so that rebindSelfView() can
      // attach the stream later when it becomes available (race: the view renders
      // the local participant tile before startVideo() finishes publishing).
      if (videoElement) {
        this.selfViewElement = videoElement;
        this.videoBindingsCache.set(videoElement.id, () => {
          videoElement.srcObject = null;
        });
      }
      const localStream = this.streamMediaPublisher.localVideoStream;
      if (videoElement && localStream) {
        logUtishaDebug('stream: binding self-view to local publisher stream');
        videoElement.srcObject = localStream;
        videoElement.muted = true;
        videoElement.play().catch(e => logUtishaError('stream: self-view play failed', e));
      } else {
        logUtishaDebug('stream: handleAttachVideo: no local stream for self-view yet, will bind on stream change');
      }
      return;
    }

    // Bind video tracks for remote participants
    // https://getstream.io/video/docs/javascript/basics/quickstart/#render-video-and-play-audio
    if (videoElement && participant && participant.data.publishedTracks.includes(2)) { // 2 = Video
      videoElement.dataset.sessionId = participant.data.sessionId;
      const untrack = this.call.trackElementVisibility(
        videoElement,
        participant.data.sessionId,
        'videoTrack',
      );

      this.videoTrackingCache.set(videoElement.id, untrack);
      // registers subscription updates and stream changes
      logUtishaDebug('stream: binding video to video with id ' + videoElement.id + ' and sessionId ' + participant.data.sessionId);
      const unbind = this.call.bindVideoElement(
        videoElement,
        participant.data.sessionId,
        'videoTrack',
      );
      // keep reference to unbind function to call it later
      this.videoBindingsCache.set(videoElement.id, unbind);
    } else {
      logUtishaDebug('stream: handleAttachVideo: no video track');
    }
  }

  /**
   * Re-binds the self-view video element to the current local video stream.
   * Must be called after any operation that changes the published stream
   * (e.g. switchCamera) because the render cycle reuses existing elements
   * and won't re-fire attachVideo for already-rendered participants.
   */
  private rebindSelfView() {
    if (!this.selfViewElement) {
      return;
    }
    const localStream = this.streamMediaPublisher.localVideoStream;
    if (localStream) {
      logUtishaDebug('stream: rebinding self-view to new local publisher stream');
      this.selfViewElement.srcObject = localStream;
      this.selfViewElement.play().catch(e => logUtishaError('stream: self-view rebind play failed', e));
    } else {
      logUtishaDebug('stream: rebindSelfView: no local stream available yet');
    }
  }

  private handleDetachVideo(payload: DetachVideoRequest) {
    logUtishaDebug('stream: handleDetachVideo');
    const {element} = payload;
    element.removeAttribute('data-session-id');
    const unbindVideo = this.videoBindingsCache.get(element.id);

    if (unbindVideo) {
      try {
        unbindVideo();
      } catch (e) { /* ignore */
      }
      this.videoBindingsCache.delete(element.id);
    }
    const untrackVideo = this.videoTrackingCache.get(element.id);
    if (untrackVideo) {
      try {
        untrackVideo();
      } catch (e) { /* ignore */
      }
      logUtishaDebug('stream: untrack video');
      this.videoTrackingCache.delete(element.id);
    }
  }

  attachAudio(participant: StreamVideoParticipant) {
    logUtishaDebug(`stream: attachAudio for participant ${participant.userId}`);
    // We don't render audio for local participant
    if (participant.isLocalParticipant) {
      return;
    }
    const id = `audio-${participant.sessionId}`;
    let audioEl = document.getElementById(id) as HTMLAudioElement | null;
    if (!audioEl) {
      audioEl = document.createElement('audio');
      audioEl.id = id;
      audioEl.dataset.sessionId = participant.sessionId;
      this.containerElement.appendChild(audioEl);
      // registers subscription updates and stream changes for audio
      const unbind = this.call.bindAudioElement(audioEl, participant.sessionId);
      // keep reference to unbind function to call it later
      this.audioBindingsCache.set(id, unbind);

      // Set up audio analyzer to monitor volume levels
      if (!audioEl.paused && audioEl.srcObject) {
        this.setupAudioVolumeMonitor(audioEl, participant.userId);
      }

      // Listen for stream changes to set up volume monitoring when stream becomes available
      audioEl.addEventListener('playing', () => {
        if (audioEl?.srcObject) {
          this.setupAudioVolumeMonitor(audioEl, participant.userId);
        }
      });
    }
  }

  private getOrCreateSharedAudioContext(): AudioContext {
    if (!this.sharedAudioContext || this.sharedAudioContext.state === 'closed') {
      this.sharedAudioContext = new AudioContext();
    }
    // Resume if suspended (browser autoplay policy)
    if (this.sharedAudioContext.state === 'suspended') {
      this.sharedAudioContext.resume().catch(err =>
        logUtishaWarn('Failed to resume shared AudioContext: ' + err)
      );
    }
    return this.sharedAudioContext;
  }

  private setupAudioVolumeMonitor(audioEl: HTMLAudioElement, userId: UserIdType) {
    try {
      const monitorKey = audioEl.id;

      // Tear down any existing monitor for this element to avoid duplicates
      const existing = this.audioMonitorSources.get(monitorKey);
      if (existing) {
        existing.source.disconnect();
        this.audioMonitorSources.delete(monitorKey);
      }

      const audioContext = this.getOrCreateSharedAudioContext();
      const source = audioContext.createMediaStreamSource(audioEl.srcObject as MediaStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      // Store reference for cleanup
      this.audioMonitorSources.set(monitorKey, { source, analyser });

      // Buffer for analyser data
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const getAverageVolume = () => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        let volume = sum / dataArray.length / 255;
        if (volume > 0 && volume < 0.1) {
          volume = 0.1 + (volume * 0.5);
        }
        return volume;
      };

      let lastUpdateTime = 0;
      const checkVolume = () => {
        if (this._audioMonitorDestroyed) return;
        if (this.audioMonitorThrottleMs === 0) {
          setTimeout(() => {
            if (document.getElementById(audioEl.id) && !audioEl.paused && audioEl.srcObject) {
              requestAnimationFrame(checkVolume);
            }
          }, 1000);
          return;
        }
        const now = Date.now();
        if (now - lastUpdateTime >= this.audioMonitorThrottleMs) {
          const volume = getAverageVolume();
          this.sessionStore.update({ ui: { audioVolume: {userId, volume} } });
          lastUpdateTime = now;
        }
        if (document.getElementById(audioEl.id) && !audioEl.paused && audioEl.srcObject) {
          requestAnimationFrame(checkVolume);
        } else {
          source.disconnect();
          this.audioMonitorSources.delete(monitorKey);
        }
      };

      requestAnimationFrame(checkVolume);
    } catch (error) {
      logUtishaError('Error setting up audio volume monitor:', error);
    }
  }

  detachAudio(sessionId: string) {
    logUtishaDebug(`stream: detachAudio for participant ${sessionId}`);
    const id = `audio-${sessionId}`;
    const unbindAudio = this.audioBindingsCache.get(id);
    if (unbindAudio) {
      unbindAudio();
      this.audioBindingsCache.delete(id);
    }
  }

  cleanupStaleAudio(newSessionIds: string[]) {
    logUtishaDebug('stream: cleanupStaleAudio');
    this.containerElement
      .querySelectorAll<HTMLMediaElement>('audio')
      .forEach((el) => {
        const sessionId = el.dataset.sessionId!;
        if (!newSessionIds.includes(sessionId)) {
          this.detachAudio(sessionId);
          el.remove();
        }
      });
  }

  private setupEventListeners() {
    logUtishaDebug('stream: setupEventListeners');
    // Handle participant updates and video binding
    this.subscriptions.push(this.call.state.participants$
      // .pipe(debounce(() => interval(150)))
      .subscribe((participants: StreamVideoParticipant[]) => {
        const streamParticipants = participants.map((p) =>
          StreamSessionParticipant.fromParticipant(p, this.sessionStore.snapshot.localUser.id!)
        ).sort((a, b) => String(a.userId).localeCompare(String(b.userId))); // Sort participants by userId as string
        try{
          if (this.audioChanged(this.sessionStore.snapshot.participants, streamParticipants)) {
            logUtishaDebug(() => 'stream: audio changed, updating audio elements');
            for (const participant of participants) {
              this.attachAudio(participant);
            }
            this.cleanupStaleAudio(participants.map(p => p.sessionId));
          }
        } catch (e) {
          logUtishaError('Error updating audio bindings for participants:', e);
        }
        this.sessionStore.update({ participants: streamParticipants });
      }));

    // Subscribe to dominant speaker updates
    this.subscriptions.push(this.call.state.dominantSpeaker$.pipe(
      throttle(() => interval(this.activeSpeakerUpdateInterval))
    ).subscribe((dominantSpeaker: StreamVideoParticipant | undefined) => {
      if (dominantSpeaker) {
        this.sessionStore.update({ ui: { activeSpeakers: [dominantSpeaker.userId] } });
      } else {
        this.sessionStore.update({ ui: { activeSpeakers: [] } });
      }
    }));

    this.callEventUnsubscribers.push(this.call.on('connection.error', (event) => {
      const errorMessage = event?.error?.message || JSON.stringify(event);
      this.handleConnectionError(errorMessage);
    }));

    this.callEventUnsubscribers.push(this.call.on('call.session_ended', (event) => {
      logUtishaInfo(`stream: session ${event.call_cid} ended`);
      this.sessionStore.update({ session: { isEnded: true } });
    }));

    this.callEventUnsubscribers.push(this.call.on('connectionQualityChanged', (event) => {
      const currentUserUpdate = event.connectionQualityUpdates
        .find(cqu => cqu.userId === this.call.currentUserId);
      // conservative estimates before enough RTT/jitter samples are collected.
      if (this.joinedAt && Date.now() - this.joinedAt < 5000) {
        return;
      }

      if (currentUserUpdate && currentUserUpdate.connectionQuality < 2) {
        this.poorQualityCount++;
        logUtishaDebug(`stream: poor network quality (quality=${currentUserUpdate.connectionQuality}, count=${this.poorQualityCount})`);
        // Require 3 consecutive poor quality reports before degrading to avoid
        // oscillation from transient SFU estimates.
        if (!this.qualityReducedMode && this.poorQualityCount >= 3) {
          this.qualityReducedMode = true;
          logUtishaWarn(SessionBreadcrumb.STREAM_REDUCING_QUALITY);
          this.streamMediaPublisher.adaptCanvasResolution('minimal');
          this.sessionStore.update({ session: { error: 'Connection quality is poor. Video quality has been reduced.' } });
        }
      } else if (currentUserUpdate) {
        if (this.qualityReducedMode && currentUserUpdate.connectionQuality >= 2) {
          // Require quality to stay good — only recover after count fully drains
          this.poorQualityCount = Math.max(0, this.poorQualityCount - 1);
          if (this.poorQualityCount === 0) {
            this.qualityReducedMode = false;
            logUtishaInfo('stream: connection quality recovered, restoring video quality');
            this.streamMediaPublisher.adaptCanvasResolution('normal');
            this.sessionStore.update({ session: { error: null } });
          }
        } else {
          this.poorQualityCount = Math.max(0, this.poorQualityCount - 1);
        }
      }
      // Log other users' quality issues for debugging
      const otherUsers = event.connectionQualityUpdates.filter(cqu => cqu.userId !== this.call.currentUserId && cqu.connectionQuality < 2);
      if (otherUsers.length > 0) {
        logUtishaDebug(`stream: poor network quality for users: ${otherUsers.map(cqu => `${cqu.userId} (quality=${cqu.connectionQuality})`).join(', ')}`);
      }
    }));


    this.callEventUnsubscribers.push(this.call.on('connection.ok', (event) => {
      // force UI refresh
      this.sessionStore.update({ participants: this.sessionStore.snapshot.participants });
    }));

    this.callEventUnsubscribers.push(this.call.on('call.user_muted', (event) => {
      if (this.call.currentUserId && event.from_user_id !== this.call.currentUserId) {
        this.sessionStore.update({ localUser: { isMuted: true } });
      }

    }));
    this.callEventUnsubscribers.push(this.call.on('call.ended', async (event) => {
      // force UI refresh
      await this.call.leave();
    }));

    const trackTypeToText = (trackType: TrackType) => {
      switch (trackType) {
        case 0:
          return 'unspecified';
        case 1:
          return 'audio';
        case 2:
          return 'video';
        case 3:
          return 'screen share';
        case 4:
          return 'screen share audio';
      }
    };
    this.callEventUnsubscribers.push(this.call.on('trackPublished', async (event) => {
      logUtishaDebug(`stream: ${trackTypeToText(event.type)} track published for user ${event.userId}`);
      // Monitor local tracks for unexpected disconnection
      if (event.userId === this.call.currentUserId) {
        setTimeout(() => this.setupTrackEndedHandlers(), 500);
      }
    }));
    this.callEventUnsubscribers.push(this.call.on('trackUnpublished', async (event) => {
      logUtishaDebug(`stream: ${trackTypeToText(event.type)} track unpublished for user ${event.userId}`);
    }));
    this.callEventUnsubscribers.push(this.call.on('error', async (event) => {
      logUtishaError(`stream: error: ${JSON.stringify(event, null, 2)}`);
    }));
    this.callEventUnsubscribers.push(this.call.on('iceRestart', async (event) => {
      logUtishaDebug(`stream: iceRestart: ${JSON.stringify(event, null, 2)}`);
    }));
    this.callEventUnsubscribers.push(this.call.on('iceTrickle', async (event) => {
      logUtishaTrace(`stream: iceTrickle: ${JSON.stringify(event, null, 2)}`);
    }));
    this.callEventUnsubscribers.push(this.call.on('changePublishQuality', async (event) => {
      logUtishaTrace(`stream: changePublishQuality: ${JSON.stringify(event, null, 2)}`);
    }));
    this.callEventUnsubscribers.push(this.call.on('changePublishOptions', async (event) => {
      logUtishaTrace(`stream: changePublishOptions: ${JSON.stringify(event, null, 2)}`);
    }));
    this.callEventUnsubscribers.push(this.call.on('goAway', async (event) => {
      logUtishaWarn(`${SessionBreadcrumb.STREAM_GO_AWAY}: ${JSON.stringify(event, null, 2)}`);
    }));

    // Handle connection state changes using callingState$
    this.subscriptions.push(this.call.state.callingState$.pipe(
      throttle(() => interval(250)) // Throttle to avoid rapid updates
    ).subscribe((callingState: CallingState) => {
      let status: ConnectionStatus | undefined;

      switch (callingState) {
        case CallingState.JOINED:
          status = ConnectionStatus.Connected;
          logUtishaDebug('stream: joined');
          // Reset reconnection state on successful join
          this.reconnectAttempts = 0;
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          this.transientErrorCount = 0;
          this.joinedAt = Date.now();
          // Verify and republish tracks after reconnection
          if (this.wasReconnecting) {
            this.wasReconnecting = false;
            logUtishaInfo('stream: connection recovered, verifying local tracks');
            this.verifyAndRepublishLocalTracks();
          }
          break;
        case CallingState.MIGRATING:
        case CallingState.RECONNECTING:
          status = ConnectionStatus.Reconnecting;
          this.wasReconnecting = true;
          logUtishaWarn(SessionBreadcrumb.STREAM_RECONNECTING);
          break;
        case CallingState.RECONNECTING_FAILED:
          logUtishaError(`${SessionBreadcrumb.STREAM_RECONNECT_FAILED}: ${callingState}`);
          this.wasReconnecting = true;
          status = ConnectionStatus.Reconnecting;
          this.attemptConnectionRecovery();
          break;
        case CallingState.OFFLINE:
        case CallingState.LEFT:
          if (callingState === CallingState.OFFLINE) {
            logUtishaError(`${SessionBreadcrumb.STREAM_RECONNECT_FAILED}: ${callingState}`);
          }
          status = ConnectionStatus.Disconnected;
          logUtishaDebug('stream: left');
          this.sessionStore.update({ session: { isEnded: true } });
          break;
        default:
          status = undefined;
          break;
      }

      if (status) {
        this.sessionStore.update({ connectionStatus: status });
      }
    }));

    this.subscriptions.push(this.performanceMonitor.pressure$.subscribe((level) => {
      this.handleCpuPressureChange(level);
    }));
  }

  private async initializeDevices() {
    // First try to get devices from the store for immediate UI update
    try {
      const cachedDevices = await this.store.getAllMediaDevices();
      if (cachedDevices) {
        logUtishaDebug('stream: using cached media devices from store');
        // Update state with cached devices for immediate display
        this.sessionStore.update({ devices: { cameras: cachedDevices.cameras, microphones: cachedDevices.microphones, speakers: cachedDevices.speakers } });
      }
    } catch (error: any) {
      logUtishaError('Error retrieving cached devices:', error);
    }

    // Always fetch the most current devices from the browser (with 5s timeout)
    try {
      const ENUMERATE_TIMEOUT = 5000;
      const devices = await Promise.race([
        navigator.mediaDevices.enumerateDevices(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('enumerateDevices timed out')), ENUMERATE_TIMEOUT)
        ),
      ]);

      const cameras = devices
        .filter((d) => d.kind === 'videoinput')
        .map((d) => ({deviceId: d.deviceId, label: d.label}));
      const microphones = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({deviceId: d.deviceId, label: d.label}));
      const speakers = devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({deviceId: d.deviceId, label: d.label}));

      // Update the state with the fresh devices
      this.sessionStore.update({ devices: { cameras, microphones, speakers } });

      // Store the devices for future use
      if (this.store) {
        await this.store.setAllMediaDevices({
          cameras,
          microphones,
          speakers
        });
        logUtishaDebug('stream: saved media devices to store');
      }
    } catch (error: any) {
      logUtishaWarn('stream: enumerateDevices failed or timed out, continuing with cached devices: ' + error?.message);
    }
  }

  private registerRequestHandlers() {
    this.subscriptions.push(this.commands.startOrStopVideo.requestTx.subscribe((payload) => {
      payload?.started ? this.startVideo(payload.device) : this.stopVideo();
    }));
    this.subscriptions.push(this.commands.startOrStopAudio.requestTx.subscribe((payload) => {
      payload?.started ? this.startAudio(payload) : this.stopAudio();
    }));
    this.subscriptions.push(this.commands.toggleMute.requestTx.subscribe((payload) => {
      if (payload) {
        payload.muted ? this.muteAudio(payload.userId) : this.unmuteAudio(payload.userId);
      }
    }));
    this.subscriptions.push(this.commands.toggleMuteAll.requestTx.subscribe((muteAll) => {
      muteAll ? this.muteAll() : this.unmuteAll();
    }));
    this.subscriptions.push(this.commands.switchCamera.requestTx.subscribe(async (device) => {
      if (device) {
        await this.switchCamera(device);
      }
    }));
    this.subscriptions.push(this.commands.switchMicrophone.requestTx.subscribe(async (device) => {
      if (device) {
        await this.switchMicrophone(device);
      }
    }));
    this.subscriptions.push(this.commands.switchSpeaker.requestTx.subscribe(async (device) => {
      if (device) {
        await this.switchSpeaker(device);
      }
    }));
    this.subscriptions.push(this.commands.refreshCameras.requestTx.subscribe(() => this.refreshCameras()));
    this.subscriptions.push(this.commands.refreshMicrophones.requestTx.subscribe(() => this.refreshMicrophones()));
    this.subscriptions.push(this.commands.refreshSpeakers.requestTx.subscribe(() => this.refreshSpeakers()));
    this.subscriptions.push(this.commands.attachVideo.requestTx.subscribe((payload) => payload ? this.handleAttachVideo(payload) : null));
    this.subscriptions.push(this.commands.detachVideo.requestTx.subscribe((payload) => payload ? this.handleDetachVideo(payload) : null));

  }
  private async enqueueDeviceOperation<T>(id: string, type: string, fn: () => Promise<T>): Promise<T> {
    if (!this.operationQueue) {
      return fn();
    }
    return this.operationQueue.enqueue({
      id,
      type,
      execute: async () => fn(),
    });
  }

  async startVideo(device?: MediaDevice): AsyncSessionResult {
    return this.enqueueDeviceOperation(`start-video-${Date.now()}`, 'media', async () => {
      logUtishaDebug(() => 'stream: startVideo with device ' + device?.deviceId);
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      try {
        // Update the camera device in the store BEFORE publishing, so that
        // StreamMediaPublisher.streamCameraToHiddenHTMLVideoElement() picks up
        // the correct deviceId when acquiring the camera stream.
        if (device) {
          this.sessionStore.update({ localUser: { camera: device } });
        }
        // Delegate to the publisher which acquires the camera, renders to
        // canvas, and publishes via call.publish() — the only path that
        // actually makes video visible to participants.
        await this.streamMediaPublisher.streamCameraUsingCanvas();
        // streamCameraUsingCanvas already sets isVideoOn=true via publishCanvasStream,
        // but we also need to track the device and reset failure counters.
        if (device) {
          this.sessionStore.update({ localUser: { camera: device } });
        }
        this.videoFailureCount = 0;
        if (this.sessionStore.snapshot?.session?.audioOnlyMode) {
          this.sessionStore.update({ session: { audioOnlyMode: false } });
        }
        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('Error starting video:', error);
        this.videoFailureCount++;
        if (this.videoFailureCount >= 2) {
          this.sessionStore.update({ session: { audioOnlyMode: true } });
          logUtishaWarn(SessionBreadcrumb.STREAM_AUDIO_ONLY_FALLBACK);
          this.sessionStore.update({ session: { error: 'Video failed repeatedly. Running in audio-only mode. You can retry via the camera button.' } });
        } else {
          this.sessionStore.update({ session: { error: 'Failed to start video' } });
        }
        return SessionResult.failure('Failed to start video');
      }
    });
  }
  async stopVideo(): AsyncSessionResult {
    return this.enqueueDeviceOperation(`stop-video-${Date.now()}`, 'media', async () => {
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      try {
        await this.streamMediaPublisher.stopVideo();
        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('Error stopping video:', error);
        this.sessionStore.update({ session: { error: 'Failed to stop video' } });
        return SessionResult.failure('Failed to stop video');
      }
    });
  }

  async startAudio(options: { started: boolean; device?: MediaDevice; muted?: boolean }): AsyncSessionResult {
    return this.enqueueDeviceOperation(`start-audio-${Date.now()}`, 'media', async () => {
      logUtishaDebug(() => 'stream: startAudio with device ' + options.device?.deviceId);
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      try {
        if (options.device) {
          await this.call.microphone.select(options.device.deviceId);
        }
        await this.call.microphone.enable();
        if (options.muted) {
          await this.call.microphone.disable();
        }
        this.sessionStore.update({ localUser: { isAudioOn: true, ...(options.device ? { microphone: options.device } : {}), isMuted: !!options.muted } });
        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('Error starting audio:', error);
        this.sessionStore.update({ session: { error: 'Failed to start audio' } });
        return SessionResult.failure('Failed to start audio');
      }
    });
  }

  async stopAudio(): AsyncSessionResult {
    return this.enqueueDeviceOperation(`stop-audio-${Date.now()}`, 'media', async () => {
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      try {
        await this.call.microphone.disable();
        this.sessionStore.update({ localUser: { isAudioOn: false, isMuted: true } });
        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('Error stopping audio:', error);
        this.sessionStore.update({ session: { error: 'Failed to stop audio' } });
        return SessionResult.failure('Failed to stop audio');
      }
    });
  }

  async muteAudio(userId?: number): AsyncSessionResult {
    if (!this.call) {
      return SessionResult.failure('Call not initialized');
    }
    try {
      if (userId) {
        await this.call.sendCustomEvent({type: 'mute', data: {userId}});
      } else {
        await this.call.microphone.disable();
      }
      this.sessionStore.update({ localUser: { isMuted: true } });
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error muting audio:', error);
      this.sessionStore.update({ session: { error: 'Failed to mute audio' } });
      return SessionResult.failure('Failed to mute audio');
    }
  }

  async unmuteAudio(userId?: number): AsyncSessionResult {
    if (!this.call) {
      return SessionResult.failure('Call not initialized');
    }
    try {
      if (userId) {
        await this.call.sendCustomEvent({type: 'unmute', data: {userId}});
      } else {
        await this.call.microphone.enable();
      }
      this.sessionStore.update({ localUser: { isMuted: false } });
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error unmuting audio:', error);
      this.sessionStore.update({ session: { error: 'Failed to unmute audio' } });
      return SessionResult.failure('Failed to unmute audio');
    }
  }

  async muteAll(): AsyncSessionResult {
    if (!this.call) {
      return SessionResult.failure('Call not initialized');
    }
    try {
      await this.call.muteAllUsers('audio');
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error muting all:', error);
      this.sessionStore.update({ session: { error: 'Failed to mute all' } });
      return SessionResult.failure('Failed to mute all');
    }
  }

  async unmuteAll(): AsyncSessionResult {
    if (!this.call) {
      return SessionResult.failure('Call not initialized');
    }
    try {
      await this.call.sendCustomEvent({type: 'unmuteAll'});
      this.sessionStore.update({ ui: { isMuteAll: false } });
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error unmuting all:', error);
      this.sessionStore.update({ session: { error: 'Failed to unmute all' } });
      return SessionResult.failure('Failed to unmute all');
    }
  }

  async switchCamera(device: MediaDevice): AsyncSessionResult {
    return this.enqueueDeviceOperation(`switch-camera-${Date.now()}`, 'device', async () => {
      logUtishaInfo(`stream: switchCamera with device ${device?.deviceId} (${device?.label})`);
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      
      // Validate device exists in available cameras
      const availableCameras = this.sessionStore.snapshot.devices.cameras;
      const deviceExists = availableCameras.some(c => c.deviceId === device.deviceId);
      if (!deviceExists) {
        logUtishaWarn(`stream: camera ${device.deviceId} not found in available devices, refreshing device list`);
        await this.refreshCameras();
        // Check again after refresh
        const refreshedCameras = this.sessionStore.snapshot.devices.cameras;
        const refreshedDevice = refreshedCameras.find(c => c.deviceId === device.deviceId);
        if (!refreshedDevice) {
          logUtishaError(`stream: camera ${device.deviceId} still not available after refresh`);
          this.sessionStore.update({ session: { error: 'Selected camera is not available' } });
          return SessionResult.failure('Camera not available');
        }
      }
      
      // Store original state for rollback on failure
      const wasVideoStarted = this.sessionStore.snapshot.localUser.isVideoOn;
      const originalDevice = this.sessionStore.snapshot.localUser.camera;
      
      try {
        // Update the camera device in the store so that the publisher picks up the new deviceId
        this.sessionStore.update({ localUser: { camera: device } });
        // If video was already started, restart the canvas pipeline with the new camera
        if (wasVideoStarted) {
          logUtishaInfo('stream: video was active, restarting publisher with new camera');
          await this.streamMediaPublisher.stopVideo();
          await this.streamMediaPublisher.streamCameraUsingCanvas();
          this.rebindSelfView();
        }
        logUtishaInfo(`stream: successfully switched to camera ${device.label}`);
        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('stream: error switching camera:', error);
        if (wasVideoStarted && originalDevice) {
          logUtishaWarn('stream: rolling back to original camera after switch failure');
          try {
            this.sessionStore.update({ localUser: { camera: originalDevice } });
            await this.streamMediaPublisher.stopVideo();
            await this.streamMediaPublisher.streamCameraUsingCanvas();
          } catch (rollbackError) {
            logUtishaError('stream: rollback failed:', rollbackError);
          }
        }
        this.sessionStore.update({ session: { error: 'Failed to switch camera' } });
        return SessionResult.failure('Failed to switch camera');
      }
    });
  }

  async switchMicrophone(device: MediaDevice): AsyncSessionResult {
    return this.enqueueDeviceOperation(`switch-mic-${Date.now()}`, 'device', async () => {
      logUtishaInfo(`stream: switchMicrophone with device ${device?.deviceId} (${device?.label})`);
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      
      // Validate device exists in available microphones
      const availableMicrophones = this.sessionStore.snapshot.devices.microphones;
      const deviceExists = availableMicrophones.some(m => m.deviceId === device.deviceId);
      if (!deviceExists) {
        logUtishaWarn(`stream: microphone ${device.deviceId} not found in available devices, refreshing device list`);
        await this.refreshMicrophones();
        // Check again after refresh
        const refreshedMicrophones = this.sessionStore.snapshot.devices.microphones;
        const refreshedDevice = refreshedMicrophones.find(m => m.deviceId === device.deviceId);
        if (!refreshedDevice) {
          logUtishaError(`stream: microphone ${device.deviceId} still not available after refresh`);
          this.sessionStore.update({ session: { error: 'Selected microphone is not available' } });
          return SessionResult.failure('Microphone not available');
        }
      }
      
      // Store original state for rollback on failure
      const wasAudioStarted = this.sessionStore.snapshot.localUser.isAudioOn;
      const wasMuted = this.sessionStore.snapshot.localUser.isMuted;
      const originalDevice = this.sessionStore.snapshot.localUser.microphone;
      
      try {
        // Select the new microphone device
        await this.call.microphone.select(device.deviceId);
        
        // If audio was already started and not muted, we need to re-enable it to publish the new track
        // This ensures the audio stream uses the new microphone
        if (wasAudioStarted && !wasMuted) {
          logUtishaInfo('stream: audio was active, re-enabling to publish new microphone track');
          await this.call.microphone.enable();
        }
        
        this.sessionStore.update({ localUser: { microphone: device } });
        logUtishaInfo(`stream: successfully switched to microphone ${device.label}`);
        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('stream: error switching microphone:', error);
        
        // Rollback: restore original microphone on failure
        if (wasAudioStarted && originalDevice) {
          logUtishaWarn('stream: rolling back to original microphone after switch failure');
          try {
            await this.call.microphone.select(originalDevice.deviceId);
            if (!wasMuted) {
              await this.call.microphone.enable();
            }
            this.sessionStore.update({ localUser: { microphone: originalDevice } });
          } catch (rollbackError) {
            logUtishaError('stream: rollback failed:', rollbackError);
          }
        }
        
        this.sessionStore.update({ session: { error: 'Failed to switch microphone' } });
        return SessionResult.failure('Failed to switch microphone');
      }
    });
  }

  async switchSpeaker(device: MediaDevice): AsyncSessionResult {
    return this.enqueueDeviceOperation(`switch-speaker-${Date.now()}`, 'device', async () => {
      logUtishaInfo(`stream: switchSpeaker with device ${device?.deviceId} (${device?.label})`);
      
      // Validate device exists in available speakers
      const availableSpeakers = this.sessionStore.snapshot.devices.speakers;
      const deviceExists = availableSpeakers.some(s => s.deviceId === device.deviceId);
      if (!deviceExists) {
        logUtishaWarn(`stream: speaker ${device.deviceId} not found in available devices, refreshing device list`);
        await this.refreshSpeakers();
        // Check again after refresh
        const refreshedSpeakers = this.sessionStore.snapshot.devices.speakers;
        const refreshedDevice = refreshedSpeakers.find(s => s.deviceId === device.deviceId);
        if (!refreshedDevice) {
          logUtishaError(`stream: speaker ${device.deviceId} still not available after refresh`);
          this.sessionStore.update({ session: { error: 'Selected speaker is not available' } });
          return SessionResult.failure('Speaker not available');
        }
      }
      
      try {
        this.sessionStore.update({ localUser: { speaker: device } });

        const audioElements = this.containerElement.querySelectorAll<HTMLAudioElement>('audio');
        for (const audioEl of Array.from(audioElements)) {
          if (typeof audioEl.setSinkId === 'function') {
            try {
              await audioEl.setSinkId(device.deviceId);
            } catch (sinkError) {
              logUtishaWarn(`Failed to set sink ID on audio element ${audioEl.id}: ${sinkError}`);
            }
          }
        }

        return SessionResult.ok;
      } catch (error: any) {
        logUtishaError('Error switching speaker:', error);
        this.sessionStore.update({ session: { error: 'Failed to switch speaker' } });
        return SessionResult.failure('Failed to switch speaker');
      }
    });
  }

  async refreshCameras(): Promise<void> {
    logUtishaInfo('stream: refreshing camera devices');
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices
        .filter((d) => d.kind === 'videoinput')
        .map((d) => ({deviceId: d.deviceId, label: d.label}));
      this.sessionStore.update({ devices: { cameras } });
      logUtishaInfo('stream: found ' + cameras.length + ' camera devices');

      // Update the cameras in the store while preserving other device types
      if (this.store) {
        try {
          const allDevices = await this.store.getAllMediaDevices();
          const existingDevices = allDevices || {microphones: [], speakers: []};
          await this.store.setAllMediaDevices({
            ...existingDevices,
            cameras
          });
        } catch (storeError) {
          logUtishaWarn('stream: failed to persist cameras to store: ' + storeError);
        }
      }
    } catch (error: any) {
      logUtishaError('stream: error refreshing cameras:', error);
      // Don't throw - device refresh should be non-blocking
    }
  }

  async refreshMicrophones(): Promise<void> {
    logUtishaInfo('stream: refreshing microphone devices');
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({deviceId: d.deviceId, label: d.label}));
      this.sessionStore.update({ devices: { microphones } });
      logUtishaInfo('stream: found ' + microphones.length + ' microphone devices');

      // Update the microphones in the store while preserving other device types
      if (this.store) {
        try {
          const allDevices = await this.store.getAllMediaDevices();
          const existingDevices = allDevices || {cameras: [], speakers: []};
          await this.store.setAllMediaDevices({
            ...existingDevices,
            microphones
          });
        } catch (storeError) {
          logUtishaWarn('stream: failed to persist microphones to store: ' + storeError);
        }
      }
    } catch (error: any) {
      logUtishaError('stream: error refreshing microphones:', error);
      // Don't throw - device refresh should be non-blocking
    }
  }

  async refreshSpeakers(): Promise<void> {
    logUtishaInfo('stream: refreshing speaker devices');
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const speakers = devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({deviceId: d.deviceId, label: d.label}));
      this.sessionStore.update({ devices: { speakers } });
      logUtishaInfo('stream: found ' + speakers.length + ' speaker devices');

      // Update the speakers in the store while preserving other device types
      if (this.store) {
        try {
          const allDevices = await this.store.getAllMediaDevices();
          const existingDevices = allDevices || {cameras: [], microphones: []};
          await this.store.setAllMediaDevices({
            ...existingDevices,
            speakers
          });
        } catch (storeError) {
          logUtishaWarn('stream: failed to persist speakers to store: ' + storeError);
        }
      }
    } catch (error: any) {
      logUtishaError('stream: error refreshing speakers:', error);
      // Don't throw - device refresh should be non-blocking
    }
  }


  private enableNoiseCancellation() {
    // lazy-load the audio filters because it's a huge package
    import('@stream-io/audio-filters-web').then((mod) => {
      // Handle CJS/ESM interop: dynamic import() of a CJS module may wrap exports under .default
      const audioFilters = (mod as any).default ?? mod;
      // Under investigation: https://github.com/GetStream/stream-video-js/issues/1786
      this.ngZone.runOutsideAngular(() => {
        this.noiseCancellation = new audioFilters.NoiseCancellation();
        const isSupported = this.noiseCancellation.isSupported();
        (typeof isSupported === 'boolean' ? Promise.resolve(isSupported) : isSupported)
          .then(async (supported: boolean) => {
            if (supported) {
              await this.noiseCancellation.init();
              // Signal track-ended handlers to ignore the old track ending.
              // registerFilter() inside enableNoiseCancellation() intentionally
              // stops the current track and creates a new filtered one.
              this.audioFilterAppliedAt = Date.now();
              try {
                await this.call.microphone.enableNoiseCancellation(this.noiseCancellation);
              } finally {
                // Keep the timestamp — the track 'ended' event fires asynchronously
                // after this block completes. The handler checks elapsed time.
              }
              // Re-register track-ended handlers on the new filtered track
              this.setupTrackEndedHandlers();
              logUtishaDebug('stream: noise cancellation enabled');
            } else {
              logUtishaDebug('stream: noise cancellation NOT supported');
            }
          });
      });
    }).catch(e => logUtishaWarn('stream: noise cancellation unavailable: ' + e));
  }

  private deviceChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private setupDeviceChangeListener(): void {
    this.deviceChangeHandler = () => {
      // Debounce rapid device changes to avoid race conditions
      if (this.deviceChangeDebounceTimer) {
        clearTimeout(this.deviceChangeDebounceTimer);
      }
      
      this.deviceChangeDebounceTimer = setTimeout(async () => {
        logUtishaInfo('stream: device change detected, re-enumerating devices');
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameras = devices
            .filter((d) => d.kind === 'videoinput')
            .map((d) => ({deviceId: d.deviceId, label: d.label}));
          const microphones = devices
            .filter((d) => d.kind === 'audioinput')
            .map((d) => ({deviceId: d.deviceId, label: d.label}));
          const speakers = devices
            .filter((d) => d.kind === 'audiooutput')
            .map((d) => ({deviceId: d.deviceId, label: d.label}));

          this.sessionStore.update({ devices: { cameras, microphones, speakers } });

          // Handle device disconnections through operation queue to prevent race conditions
          await this.handleDeviceDisconnection(cameras, microphones, speakers);

          // Update store
          if (this.store) {
            await this.store.setAllMediaDevices({ cameras, microphones, speakers });
          }
        } catch (error) {
          logUtishaError('stream: error re-enumerating devices after device change:', error);
        }
      }, 300); // 300ms debounce
    };

    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }

  private async handleDeviceDisconnection(
    cameras: MediaDevice[],
    microphones: MediaDevice[],
    speakers: MediaDevice[]
  ): Promise<void> {
    // Use operation queue if available to serialize device operations
    const enqueue = <T>(id: string, fn: () => Promise<T>): Promise<T> => {
      if (this.operationQueue) {
        return this.operationQueue.enqueue({ id, type: 'device-disconnect', execute: fn });
      }
      return fn();
    };

    // Check if active camera still exists
    const activeCamera = this.sessionStore.snapshot.localUser.camera;
    if (activeCamera && !cameras.find(c => c.deviceId === activeCamera.deviceId)) {
      logUtishaWarn(`${SessionBreadcrumb.STREAM_CAMERA_DISCONNECTED}, falling back`);
      if (cameras.length > 0) {
        await enqueue('camera-fallback', () => this.switchCamera(cameras[0]));
      } else {
        logUtishaWarn(SessionBreadcrumb.STREAM_NO_CAMERAS_LEFT);
        this.sessionStore.update({ session: { error: 'Camera disconnected and no alternatives available' } });
      }
    }

    // Check if active microphone still exists
    const activeMic = this.sessionStore.snapshot.localUser.microphone;
    if (activeMic && !microphones.find(m => m.deviceId === activeMic.deviceId)) {
      logUtishaWarn(`${SessionBreadcrumb.STREAM_MIC_DISCONNECTED}, falling back`);
      if (microphones.length > 0) {
        await enqueue('mic-fallback', () => this.switchMicrophone(microphones[0]));
      } else {
        logUtishaWarn(SessionBreadcrumb.STREAM_NO_MICS_LEFT);
        this.sessionStore.update({ session: { error: 'Microphone disconnected and no alternatives available' } });
      }
    }

    // Check if active speaker still exists
    const activeSpeaker = this.sessionStore.snapshot.localUser.speaker;
    if (activeSpeaker && !speakers.find(s => s.deviceId === activeSpeaker.deviceId)) {
      logUtishaWarn(`${SessionBreadcrumb.STREAM_SPEAKER_DISCONNECTED}, falling back`);
      if (speakers.length > 0) {
        await enqueue('speaker-fallback', () => this.switchSpeaker(speakers[0]));
      }
    }
  }

  private setupTrackEndedHandlers(): void {
    // Clean up previous handlers to avoid duplicates
    this.trackEndedCleanups.forEach(fn => fn());
    this.trackEndedCleanups = [];

    // Monitor local camera track
    const cameraTrack = this.call.camera?.state?.mediaStream?.getVideoTracks()?.[0];
    if (cameraTrack) {
      const onCameraEnded = () => {
        if (!this.sessionStore.snapshot.localUser.isVideoOn) return;
        logUtishaWarn(`${SessionBreadcrumb.STREAM_CAMERA_TRACK_ENDED} — scheduling republish`);
        this.scheduleCameraRepublish('track-ended');
      };
      cameraTrack.addEventListener('ended', onCameraEnded);
      this.trackEndedCleanups.push(() => cameraTrack.removeEventListener('ended', onCameraEnded));
    }
    // Monitor local microphone track
    const micTrack = this.call.microphone?.state?.mediaStream?.getAudioTracks()?.[0];
    if (micTrack) {
      const onMicEnded = () => {
        if (!this.sessionStore.snapshot.localUser.isAudioOn) return;
        // Noise cancellation filter registration intentionally stops the old
        // track and creates a new one (SDK's registerFilter → applySettingsToStream
        // → stopTracks). The track 'ended' event fires asynchronously and may
        // arrive after the await completes, so use a time-based guard instead
        // of a boolean flag.
        if (this.audioFilterAppliedAt && Date.now() - this.audioFilterAppliedAt < 5000) {
          logUtishaDebug('stream: mic track ended shortly after audio filter registration, ignoring');
          return;
        }
        // Verify the ended track is still the active microphone track.
        // Virtual audio devices (e.g. NDI) end their tracks when no source
        // is feeding them, but the SDK may have already moved to a new track.
        const currentMicTrack = this.call.microphone?.state?.mediaStream?.getAudioTracks()?.[0];
        if (currentMicTrack && currentMicTrack !== micTrack) {
          logUtishaDebug('stream: stale mic track ended, ignoring (active track differs)');
          return;
        }
        logUtishaWarn(`${SessionBreadcrumb.STREAM_MIC_TRACK_ENDED} — scheduling republish`);
        this.scheduleMicRepublish('track-ended');
      };
      micTrack.addEventListener('ended', onMicEnded);
      this.trackEndedCleanups.push(() => micTrack.removeEventListener('ended', onMicEnded));
    }
  }

  /**
   * Schedule a bounded, cooldown-limited republish of the local camera track.
   * Called from the camera track 'ended' handler, the MediaWatchdog stall
   * detector, and page-visibility restore. Safe to invoke repeatedly — the
   * cooldown coalesces bursts and the per-window cap stops infinite loops
   * when the device is actually dead.
   *
   * Attempt failures self-reschedule via `'retry-after-failure'`: the
   * MediaWatchdog can't pick up video failures here because it skips
   * stall detection on stopped pipelines ("don't alarm on stopped"), and
   * track-ended won't fire again on a track that no longer exists, so
   * the scheduler is the only retry path.
   */
  scheduleCameraRepublish(reason: PublishRepairReason): void {
    if (!this.sessionStore.snapshot.localUser.isVideoOn) return;
    if (this.cameraRepublishTimer) {
      // Already pending — reason is logged, but don't stack timers.
      logUtishaDebug(`stream: camera republish already pending (ignoring ${reason})`);
      return;
    }
    const now = Date.now();
    if (now - this.cameraRepublishWindowStart > this.TRACK_REPUBLISH_WINDOW_MS) {
      this.cameraRepublishWindowStart = now;
      this.cameraRepublishAttempts = 0;
    }
    if (this.cameraRepublishAttempts >= this.TRACK_REPUBLISH_MAX_PER_WINDOW) {
      logUtishaError(`stream: camera republish budget exhausted (${this.cameraRepublishAttempts} in ${this.TRACK_REPUBLISH_WINDOW_MS}ms) — giving up`);
      this.sessionStore.update({ localUser: { isVideoOn: false } });
      this.sessionStore.update({ session: { error: 'Camera was disconnected' } });
      return;
    }
    this.cameraRepublishAttempts++;
    const attempt = this.cameraRepublishAttempts;
    const delay = this.computeRepublishDelay(attempt);
    logUtishaWarn(`${SessionBreadcrumb.STREAM_CAMERA_REPUBLISH_SCHEDULED} attempt ${attempt}/${this.TRACK_REPUBLISH_MAX_PER_WINDOW} in ${delay}ms (reason=${reason})`);
    this.cameraRepublishTimer = setTimeout(async () => {
      this.cameraRepublishTimer = null;
      try {
        await this.streamMediaPublisher.stopVideo();
        await this.streamMediaPublisher.streamCameraUsingCanvas();
        logUtishaInfo(`stream: camera republish attempt ${attempt} succeeded`);
        // Success — re-register track-ended listeners on the new track.
        this.setupTrackEndedHandlers();
      } catch (e: any) {
        logUtishaWarn(`${SessionBreadcrumb.STREAM_CAMERA_REPUBLISH_FAILED} ${attempt} failed: ${e?.message || e}`);
        this.scheduleCameraRepublish('retry-after-failure');
      }
    }, delay);
  }

  /**
   * Schedule a bounded, cooldown-limited republish of the local microphone
   * track. Same shape as scheduleCameraRepublish — see that for contract.
   * Self-retries for symmetry with video; for audio the watchdog would
   * also eventually catch it via `isAudioAlive`, but only after its
   * tick + grace window, which is slower than retrying directly.
   */
  scheduleMicRepublish(reason: PublishRepairReason): void {
    if (!this.sessionStore.snapshot.localUser.isAudioOn) return;
    if (this.micRepublishTimer) {
      logUtishaDebug(`stream: mic republish already pending (ignoring ${reason})`);
      return;
    }
    const now = Date.now();
    if (now - this.micRepublishWindowStart > this.TRACK_REPUBLISH_WINDOW_MS) {
      this.micRepublishWindowStart = now;
      this.micRepublishAttempts = 0;
    }
    if (this.micRepublishAttempts >= this.TRACK_REPUBLISH_MAX_PER_WINDOW) {
      logUtishaError(`stream: mic republish budget exhausted (${this.micRepublishAttempts} in ${this.TRACK_REPUBLISH_WINDOW_MS}ms) — giving up`);
      this.sessionStore.update({ localUser: { isAudioOn: false, isMuted: true } });
      this.sessionStore.update({ session: { error: 'Microphone was disconnected' } });
      return;
    }
    this.micRepublishAttempts++;
    const attempt = this.micRepublishAttempts;
    const delay = this.computeRepublishDelay(attempt);
    logUtishaWarn(`${SessionBreadcrumb.STREAM_MIC_REPUBLISH_SCHEDULED} attempt ${attempt}/${this.TRACK_REPUBLISH_MAX_PER_WINDOW} in ${delay}ms (reason=${reason})`);
    this.micRepublishTimer = setTimeout(async () => {
      this.micRepublishTimer = null;
      try {
        // Disable then re-enable the microphone to force reacquisition of the
        // underlying track. The SDK handles publish/unpublish internally.
        if (this.call.microphone.state.status === 'enabled') {
          await this.call.microphone.disable().catch(() => {});
        }
        await this.call.microphone.enable();
        logUtishaInfo(`stream: mic republish attempt ${attempt} succeeded`);
        this.setupTrackEndedHandlers();
      } catch (e: any) {
        logUtishaWarn(`${SessionBreadcrumb.STREAM_MIC_REPUBLISH_FAILED} ${attempt} failed: ${e?.message || e}`);
        this.scheduleMicRepublish('retry-after-failure');
      }
    }, delay);
  }

  /**
   * Exponential backoff per attempt, capped, with a small jitter so
   * independent clients seeing the same wake-event (e.g. devicechange when
   * a shared USB hub blips) don't line up their retries lockstep. Attempt
   * numbers are 1-indexed.
   */
  private computeRepublishDelay(attempt: number): number {
    const base = this.TRACK_REPUBLISH_COOLDOWN_MS * Math.pow(2, attempt - 1);
    const capped = Math.min(base, this.TRACK_REPUBLISH_MAX_BACKOFF_MS);
    const jitter = 0.8 + Math.random() * 0.4; // ±20%
    return Math.round(capped * jitter);
  }

  private async verifyAndRepublishLocalTracks(): Promise<void> {
    // Re-enable camera if it was active before the interruption
    // Route through the publisher (canvas pipeline) rather than call.camera.enable()
    // to match how video is published in startVideo().
    if (this.sessionStore.snapshot.localUser.isVideoOn) {
      try {
        await this.streamMediaPublisher.stopVideo();
        await this.streamMediaPublisher.streamCameraUsingCanvas();
        logUtishaInfo('stream: re-enabled camera via publisher after reconnection');
      } catch (e: any) {
        logUtishaWarn(`${SessionBreadcrumb.STREAM_RECONNECT_CAMERA_REENABLE_FAILED}: ${e?.message || e}`);
        this.scheduleCameraRepublish('post-reconnect-verify-failed');
      }
    }

    // Re-enable microphone if it was active and not muted before the interruption
    if (this.sessionStore.snapshot.localUser.isAudioOn && !this.sessionStore.snapshot.localUser.isMuted) {
      if (this.call.microphone.state.status !== 'enabled') {
        try {
          await this.call.microphone.enable();
        } catch (e: any) {
          logUtishaWarn(`${SessionBreadcrumb.STREAM_RECONNECT_MIC_REENABLE_FAILED}: ${e?.message || e}`);
          this.scheduleMicRepublish('post-reconnect-verify-failed');
        }
      }
    }

    // Re-register track monitors after reconnection
    this.setupTrackEndedHandlers();
    logUtishaInfo('stream: track verification complete');
  }

  private async attemptConnectionRecovery(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logUtishaError('stream: max reconnection attempts reached, giving up');
      this.sessionStore.update({ session: { isEnded: true } });
      this.sessionStore.update({ connectionStatus: ConnectionStatus.Failed });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts - 1), 8000);
    logUtishaWarn(`${SessionBreadcrumb.STREAM_RECONNECT_ATTEMPT} ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.sessionStore.update({ connectionStatus: ConnectionStatus.Reconnecting });

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.call.join({create: true});
      } catch (e) {
        logUtishaWarn('stream: reconnection attempt failed: ' + e);
        this.attemptConnectionRecovery();
      }
    }, delay);
  }

  private handleCpuPressureChange(level: CpuPressureLevel): void {
    logUtishaInfo(`stream: CPU pressure level changed to '${level}'`);
    switch (level) {
      case 'normal': this.audioMonitorThrottleMs = 50; break;
      case 'reduced': this.audioMonitorThrottleMs = 200; break;
      case 'minimal': this.audioMonitorThrottleMs = 0; break;
    }
    this.streamMediaPublisher.adaptCanvasResolution(level);
    this.sessionStore.update({ session: { cpuPressure: level } });
  }

  /**
   * Classify a `connection.error` event and react: count transient
   * errors toward the 5-error "connection unstable" threshold, surface
   * fatal errors immediately. Extracted from the SDK event handler so
   * the unit suite can drive it directly.
   */
  private handleConnectionError(errorMessage: string): void {
    const isTransient = this.isTransientError(errorMessage);
    if (isTransient) {
      this.transientErrorCount++;
      logUtishaWarn(`${SessionBreadcrumb.STREAM_TRANSIENT_ERROR} (${this.transientErrorCount}): ${errorMessage}`);
      if (this.transientErrorCount >= 5) {
        logUtishaError('stream: too many transient errors, connection may be unstable');
        this.sessionStore.update({ session: { error: 'Connection is unstable. You may experience interruptions.' } });
        this.transientErrorCount = 0;
      }
    } else {
      logUtishaError(`stream: fatal connection error: ${errorMessage}`);
      this.sessionStore.update({ session: { error: 'Connection error: ' + errorMessage } });
    }
  }

  private isTransientError(message: string): boolean {
    const transientPatterns = [
      'timeout', 'timed out', 'network', 'ECONNRESET', 'ECONNREFUSED',
      'ETIMEDOUT', 'fetch failed', 'Failed to fetch', 'NetworkError',
      'AbortError', 'ice', 'ICE', 'DTLS', 'websocket'
    ];
    const lowerMessage = message.toLowerCase();
    return transientPatterns.some(pattern => lowerMessage.includes(pattern.toLowerCase()));
  }

  destroy() {
    this._audioMonitorDestroyed = true;
    this.lifecycleGuardian?.stop();
    this.lifecycleGuardian = undefined;
    this.mediaWatchdog?.stop();
    this.mediaWatchdog = undefined;
    this.performanceMonitor.destroy();
    this.operationQueue?.cancelAll('StreamSessionMedia destroyed');
    
    // Clean up device change listener and debounce timer
    if (this.deviceChangeHandler) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
      this.deviceChangeHandler = undefined;
    }
    if (this.deviceChangeDebounceTimer) {
      clearTimeout(this.deviceChangeDebounceTimer);
      this.deviceChangeDebounceTimer = null;
    }
    
    this.trackEndedCleanups.forEach(fn => fn());
    this.trackEndedCleanups = [];
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.cameraRepublishTimer) {
      clearTimeout(this.cameraRepublishTimer);
      this.cameraRepublishTimer = null;
    }
    if (this.micRepublishTimer) {
      clearTimeout(this.micRepublishTimer);
      this.micRepublishTimer = null;
    }

    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];

    this.callEventUnsubscribers.forEach(fn => fn());
    this.callEventUnsubscribers = [];

    this.selfViewElement = null;
    this.streamMediaPublisher?.destroy();

    this.videoBindingsCache.forEach(unbind => unbind?.());
    this.videoBindingsCache.clear();

    this.videoTrackingCache.forEach(untrack => untrack?.());
    this.videoTrackingCache.clear();

    this.audioBindingsCache.forEach(unbind => unbind?.());
    this.audioBindingsCache.clear();

    // Clean up audio volume monitor sources
    this.audioMonitorSources.forEach(({ source }) => {
      try { source.disconnect(); } catch (_) { /* already disconnected */ }
    });
    this.audioMonitorSources.clear();

    // Close the shared AudioContext
    if (this.sharedAudioContext && this.sharedAudioContext.state !== 'closed') {
      this.sharedAudioContext.close().catch(err =>
        logUtishaWarn('Failed to close shared AudioContext: ' + err)
      );
      this.sharedAudioContext = null;
    }

    this.containerElement
      .querySelectorAll<HTMLAudioElement>('audio')
      .forEach(el => el.remove());

    this.noiseCancellation?.dispose?.();
  }

  private audioChanged(oldParticipants: readonly SessionParticipant[], newParticipants: SessionParticipant[]) {
    const newAudioKeys = newParticipants.map(p => p.audioHashKey()).join(',');
    const oldAudioKeys = (oldParticipants || []).map(p => p.audioHashKey()).join(',');
    return newAudioKeys !== oldAudioKeys;
  }
}
